import useTranslate from '@src/i18n/useTranslate';
import React, { useEffect } from 'react';
import Input from '@src/components/ui2/Input';

import { Brands } from './styles';
import Button from '@src/components/ui2/Button';
import { MdOutlineCheckCircle } from 'react-icons/md';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { PaymentMethodFormProps } from '..';
import { zodResolver } from '@hookform/resolvers/zod';
import { schema } from './schema';
import Select from '@src/components/ui2/Select';
import { useAppSelector } from '@src/core/hooks';
import { buyerIsForeign } from '@src/features/auth';
import { PaymentCardParams } from '@src/core/buyerApiClient';
import CardBrand from '@src/components/reusable/CardBrand';

export const CardForm = (props: PaymentMethodFormProps) => {
  const { paymentOption, onSubmit, contextErrors } = props;
  const { i18n } = useTranslate();

  const isForeignBuyer = useAppSelector(buyerIsForeign);

  const { handleSubmit, control, formState, setError } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    resolver: zodResolver(schema(i18n, isForeignBuyer, paymentOption)),
    defaultValues: {
      phone: '',
      holder: '',
      holderIdentity: undefined,
      cardNumber: '',
      securityCode: '',
      validity: '',
      installments: paymentOption?.installmentOptions[0]!.division,
      postalCode: undefined,
    },
  });
  const { errors } = formState;

  const submit = (payload: FieldValues) => {
    const cardValues: PaymentCardParams = {
      holder: payload['holder'],
      holderIdentity: payload['holderIdentity'],
      cardNumber: payload['cardNumber'],
      securityCode: payload['securityCode'],
      validityMonth: payload['validityMonth'],
      validityYear: payload['validityYear'],
      installments: payload['installments'],
      postalCode: payload['postalCode'],
    };

    onSubmit(paymentOption.paymentMethod.id, cardValues);
  };

  const convertContextErrors = () => {
    for (const [key, value] of Object.entries(contextErrors)) {
      const match = /\[(\d+)\]\[([^\]]*)\]/.exec(key);

      if (match) {
        const field = match[2];

        for (const error of value) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setError(field, {
            type: 'manual',
            message: error,
          });
        }
      }
    }
  };

  useEffect(() => {
    convertContextErrors();
  }, [contextErrors]);

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Controller
        name='holder'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            value={value}
            onChange={onChange}
            label={i18n('checkout_nameAsInCard')}
            error={!!errors[name]}
            helperText={errors[name]?.message ?? i18n('direct_link_card_form_holder_helper')}
            {...rest}
          />
        )}
      />

      {!isForeignBuyer ? (
        <Controller
          name='holderIdentity'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              name={name}
              mask={['cpf', 'cnpj']}
              value={value}
              onChange={onChange}
              label={i18n('checkout_card_owner_cpf_cnpj')}
              error={!!errors[name]}
              helperText={errors[name]?.message as string}
              {...rest}
            />
          )}
        />
      ) : null}

      {!isForeignBuyer ? (
        <Controller
          name='postalCode'
          control={control}
          render={({ field: { onChange, value, name, ...rest } }) => (
            <Input
              name={name}
              value={value}
              mask={!isForeignBuyer ? 'br_postal_code' : undefined}
              onChange={onChange}
              label={i18n('checkout_postalCode')}
              error={!!errors[name]}
              helperText={errors[name]?.message as string}
              {...rest}
            />
          )}
        />
      ) : null}

      <Controller
        name='cardNumber'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            mask='card_number'
            value={value}
            onChange={onChange}
            label={i18n('checkout_cardNumber')}
            error={!!errors[name]}
            helperText={
              (errors[name]?.message as string) ?? (
                <>
                  <Brands>
                    {paymentOption.brands?.map((brand) => (
                      <CardBrand code={brand.code} key={brand.code} />
                    ))}
                  </Brands>
                </>
              )
            }
            {...rest}
          />
        )}
      />

      <Controller
        name='securityCode'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            mask='security_card_code'
            value={value}
            onChange={onChange}
            label={i18n('checkout_securityCode')}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            {...rest}
          />
        )}
      />

      <Controller
        name='validity'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Input
            name={name}
            mask='payment_card_validity'
            value={value}
            onChange={onChange}
            label={i18n('checkout_valid_date')}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            placeholder={i18n('direct_link_card_form_validity_placeholder')}
            {...rest}
          />
        )}
      />

      <Controller
        name='installments'
        control={control}
        render={({ field: { onChange, value, name, ...rest } }) => (
          <Select
            name={name}
            value={value}
            onChange={onChange}
            label={i18n('checkout_paymentIn')}
            error={!!errors[name]}
            helperText={errors[name]?.message as string}
            {...rest}
          >
            {paymentOption?.installmentOptions.map(({ division, label }) => (
              <option key={division} value={division}>
                {label}
              </option>
            ))}
          </Select>
        )}
      />

      <Button type='submit' fullWidth>
        {i18n('direct_link_card_form_submit')} <MdOutlineCheckCircle />
      </Button>
    </form>
  );
};

export default CardForm;
